'use client';

import type { Dispatch, SetStateAction } from 'react';
import classNames from 'classnames';
import { Card } from '$src-components/atoms/Card';
import { changeUrl } from '$util/index';
import type { AppGarage } from '$page-router/api/getGaragesByLatLong';
import { GarageDetailsSection } from '@/components/organisms/GDPGarageInfoCard/components/GarageDetailsSection';
import { GarageFooterSection } from '@/components/organisms/GDPGarageInfoCard/components/GarageFooterSection';
import { Button, DesignType } from '@/components/atoms/Button';
import { Img } from '@/components/atoms/Img';
import { GarageInfoCardHeader } from '@/components/atoms/GarageInfoCardHeader';
import { GarageReviewSection } from '@/components/atoms/GarageReviewSection';

import styles from './index.module.scss';

export interface GDPGarageInfoCardTranslations {
  readonly certifiedImageDesktop?: {
    readonly url: string;
    readonly alt: string;
  };
  readonly certifiedImageMobile?: {
    readonly url: string;
    readonly alt: string;
  };
  readonly reviewsNumberText: string;
  readonly moreInfoCtaLabel: string;
  readonly fixterCNDText?: string;
  readonly reviewTitle: string;
  readonly bookingsNumberText?: string;
  readonly yearsInBusinessText?: string;
}

export interface GDPGarageInfoCardProps {
  readonly garage: AppGarage;
  readonly setSelectedGarageId: Dispatch<SetStateAction<string>>;
  readonly selectedGarageId: string;
  readonly translations: GDPGarageInfoCardTranslations;
  readonly className?: string;
}

export function GDPGarageInfoCard({
  className,
  garage,
  selectedGarageId,
  setSelectedGarageId,
  translations: {
    certifiedImageDesktop,
    certifiedImageMobile,
    reviewsNumberText,
    moreInfoCtaLabel,
    fixterCNDText,
    reviewTitle,
    bookingsNumberText,
    yearsInBusinessText,
  },
  ...props
}: GDPGarageInfoCardProps): JSX.Element {
  const {
    name,
    reviewsNumber,
    ratingNumber,
    bookingsNumber,
    yearsInBusinessNumber,
    review,
    garageId,
    slug,
    isFixterGarage,
  } = garage;

  const isSelectedGarage = garageId === selectedGarageId;

  return (
    <Card
      className={classNames(
        GDPGarageInfoCard.displayName,
        styles.component,
        className,
        styles.GarageCard,
        isSelectedGarage && styles.SelectedGarage
      )}
      onMouseOver={() => {
        setSelectedGarageId(garageId);
      }}
      onClick={() => {
        changeUrl({ pathname: `/garage/${slug}` });
      }}
      elevation={2}
      cornerRadius={2}
      {...props}
    >
      {isFixterGarage && certifiedImageDesktop && (
        <div className={styles['fixter-certified-desktop-container']}>
          <Img
            alt={certifiedImageDesktop.alt}
            provider="cloudinary"
            src={certifiedImageDesktop.url}
            width={206}
            height={44}
          />
        </div>
      )}
      <div className={styles.GarageCardLeftSection}>
        <GarageInfoCardHeader
          className={styles.GarageInfoCardHeader}
          name={name}
          reviewsNumber={reviewsNumber}
          ratingNumber={ratingNumber}
          translations={{ reviewsNumberText }}
        />
        {isFixterGarage && certifiedImageMobile && (
          <div className={styles['fixter-certified-mobile-container']}>
            <Img
              alt={certifiedImageMobile.alt}
              provider="cloudinary"
              src={certifiedImageMobile.url}
              width={120}
              height={25}
            />
          </div>
        )}
        <GarageDetailsSection garage={garage} translations={{ fixterCNDText }} />
        <Button
          className={classNames('ButtonCta', styles.GarageCtaButton)}
          designType={DesignType.PRIMARY}
          href={`/garage/${slug}`}
        >
          {moreInfoCtaLabel}
        </Button>
      </div>

      <div className={styles.GarageCardRightSection}>
        {review && (
          <GarageReviewSection
            rating={review.rating}
            text={review.text}
            authorName={review.authorName}
            relativeTime={review.relativeTime}
            translations={{ reviewTitle }}
          />
        )}

        {isFixterGarage && (
          <GarageFooterSection
            bookingsNumber={bookingsNumber}
            yearsInBusinessNumber={yearsInBusinessNumber}
            translations={{ bookingsNumberText, yearsInBusinessText }}
          />
        )}
      </div>
    </Card>
  );
}

GDPGarageInfoCard.displayName = 'GDPGarageInfoCard';
